import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { SlidersHorizontal } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { CreateObjectButton } from '@/components/CreateObjectButton'
import { AuthService } from '@/services/core/auth'
import { RulesetsApi, Ruleset } from '@/services/rules/rulesetsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import type { ListSort } from '@/services/core/paging'
import { AppShell } from '../../components/AppShell'
import { RulesetTable } from '../../components/RulesetTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { CardContent, CardIcon } from '@/components/ui/card'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { useNetwork } from '@/contexts/NetworkContext'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'

export function RulesetsPage() {
  const { t } = useTranslation()
  const hasToken = AuthService.isAuthenticated()
  const [search, setSearch] = useUrlSearch()
  const { scopeKey, scopeReady } = useNetwork()
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.rulesets.status', 'ACTIVE')
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const { data, isLoading: loading } = useQuery({
    queryKey: ['rulesets', scopeKey, search, statusFilter, page, pageSize, sort],
    queryFn: () => RulesetsApi.getPage({ page, pageSize, search, status: statusFilter, sort }),
    enabled: hasToken && scopeReady,
    staleTime: 5 * 60 * 1000,
  })
  const rulesets = data?.results ?? []
  const totalCount = data?.totalCount ?? 0
  const queryClient = useQueryClient()
  const [selection, setSelection] = useState<Ruleset[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(selection.map((r) => RulesetsApi.update(r.id, { name: r.name, objectState: state })))
      queryClient.invalidateQueries({ queryKey: ['rulesets'] })
      toast.success(t('common.done'), {
        description: t(state === 'ACTIVE' ? 'rulesets.doneDetail_activate' : 'rulesets.doneDetail_deactivate', { count: selection.length }),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  return (
    <AppShell crumbs={[{ label: t('rulesets.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('rulesets.title')}</h1>
            <p>{t('rulesets.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <CreateObjectButton type="ruleset" size="default" />
          </div>
        </div>

        <ListCard tone="delivery">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={SlidersHorizontal} />
                {t('rulesets.title')}
              </>
            }
            count={totalCount}
            actions={<PageSizeSelect value={pageSize} onChange={(size) => refilter(() => setPageSize(size))} />}
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton value={statusFilter} onChange={(v) => refilter(() => setStatusFilter(v))} />
              </>
            }
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              { label: t('common.activate'), icon: 'pi pi-check-circle', severity: 'success', onClick: () => bulkSetStatus('ACTIVE') },
              { label: t('common.deactivate'), icon: 'pi pi-ban', severity: 'danger', onClick: () => bulkSetStatus('INACTIVE') },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />

          <CardContent className="p-0">
            <RulesetTable
              rulesets={rulesets}
              loading={loading}
              selection={selection}
              onSelectionChange={setSelection}
              emptyMessage={
                hasToken ? t('rulesets.noRulesetsFound') : t('common.signInToLoad')
              }
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('rulesets.rulesetCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
